import { useState } from 'react';
import {
  type FormFieldDef,
  OPTION_FIELD_TYPES,
  MASKABLE_FIELD_TYPES,
} from '@hcc/types';
import { ModalShell } from '../../ui/ModalShell';
import { Button } from '../../ui/Button';
import { OptionsEditor } from './OptionsEditor';
import { GridEditor } from './GridEditor';
import { ConditionEditor, type ConditionSourceField } from './ConditionEditor';
import { FIELD_TYPE_META } from './fieldMeta';
import { ContentConfig } from './config/ContentConfig';
import { UploadConfig } from './config/UploadConfig';
import { VisibilityConfig } from './config/VisibilityConfig';

interface FieldConfigModalProps {
  field: FormFieldDef;
  /** Other fields on the form, for the visible_if picker + value list. */
  otherFields: ConditionSourceField[];
  onSave: (next: FormFieldDef) => void;
  onClose: () => void;
}

const inputClass =
  'mt-1 block w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 py-2 text-sm';

/** Side modal to configure one field. Owns a local draft until Save. */
export function FieldConfigModal({ field, otherFields, onSave, onClose }: FieldConfigModalProps) {
  const [draft, setDraft] = useState<FormFieldDef>(field);
  const meta = FIELD_TYPE_META[draft.type];

  const isStatic =
    draft.type === 'section_heading' ||
    draft.type === 'paragraph' ||
    draft.type === 'rich_text' ||
    draft.type === 'image';
  const hasOptions = OPTION_FIELD_TYPES.includes(draft.type);

  function patch(p: Partial<FormFieldDef>) {
    setDraft((d) => ({ ...d, ...p }));
  }

  return (
    <ModalShell open onClose={onClose} titleId="field-config-title" maxWidth="max-w-lg">
      <h2 id="field-config-title" className="mb-1 text-base font-semibold text-fg">
        Configure {meta.label}
      </h2>
      <p className="mb-4 text-xs text-fg-dim">{meta.hint}</p>

      <div className="max-h-[60vh] space-y-4 overflow-y-auto pr-1">
        <label className="block text-sm">
          <span className="font-medium text-fg-muted">
            {draft.type === 'rich_text' ? 'Label (builder only)' : isStatic ? 'Text' : 'Label'}
          </span>
          <input
            value={draft.label}
            onChange={(e) => patch({ label: e.target.value })}
            className={inputClass}
          />
        </label>

        <ContentConfig draft={draft} patch={patch} />

        {!isStatic ? (
          <>
            <label className="block text-sm">
              <span className="font-medium text-fg-muted">Help text</span>
              <input
                value={draft.help ?? ''}
                onChange={(e) => patch({ help: e.target.value || undefined })}
                className={inputClass}
              />
            </label>
            <label className="flex items-center gap-2 text-sm text-fg">
              <input
                type="checkbox"
                checked={!!draft.required}
                onChange={(e) =>
                  patch({
                    required: e.target.checked,
                    ...(e.target.checked ? {} : { optional_on_event_day: undefined }),
                  })
                }
              />
              Required
            </label>
            {draft.required ? (
              <label className="ml-6 flex items-center gap-2 text-sm text-fg-muted">
                <input
                  type="checkbox"
                  checked={!!draft.optional_on_event_day}
                  onChange={(e) =>
                    patch({ optional_on_event_day: e.target.checked || undefined })
                  }
                />
                Not required on mobilisation days
              </label>
            ) : null}
            <label className="block text-sm">
              <span className="font-medium text-fg-muted">Field key</span>
              <input
                value={draft.key}
                onChange={(e) => patch({ key: e.target.value })}
                className={inputClass + ' font-mono text-xs'}
              />
              <span className="mt-1 block text-xs text-fg-dim">
                Lowercase letters, numbers and underscores. Identifies the answer in the data.
              </span>
            </label>
          </>
        ) : null}

        {hasOptions ? (
          <div>
            <div className="mb-1.5 text-sm font-medium text-fg-muted">Options</div>
            <OptionsEditor
              options={draft.options ?? []}
              onChange={(options) => patch({ options })}
            />
          </div>
        ) : null}

        {MASKABLE_FIELD_TYPES.includes(draft.type) ? (
          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Input mask (optional)</span>
            <input
              value={draft.mask ?? ''}
              onChange={(e) => patch({ mask: e.target.value || undefined })}
              className={inputClass + ' font-mono'}
              placeholder="@@ ###### @"
            />
            <span className="mt-1 block text-xs text-fg-dim">
              Restrict the format. <code>@</code> = letter, <code>#</code> = number, <code>*</code>{' '}
              = either. Other characters are literal.
            </span>
          </label>
        ) : null}

        {draft.type === 'grid' && draft.grid ? (
          <GridEditor
            grid={draft.grid}
            onChange={(grid) => patch({ grid })}
            gateFields={otherFields.filter((f) => f.type === 'yes_no')}
          />
        ) : null}

        {draft.type === 'file_upload' ? <UploadConfig draft={draft} patch={patch} /> : null}

        {!isStatic ? <VisibilityConfig draft={draft} patch={patch} /> : null}

        {!isStatic ? (
          <ConditionEditor
            condition={draft.visible_if}
            sources={otherFields}
            onChange={(c) => patch({ visible_if: c })}
            label="Only show this field conditionally"
            emptyHint="Add another question first to base a condition on it."
          />
        ) : null}
      </div>

      <div className="mt-4 flex justify-end gap-2 border-t border-line/10 pt-3">
        <Button variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" size="sm" onClick={() => onSave(draft)}>
          Done
        </Button>
      </div>
    </ModalShell>
  );
}
